import {
  useCallback,
  useMemo,
  useRef,
} from 'react';
import * as is from 'is';
import { saveSource, getSources } from '@utils/graphql/creators/sources';
import { getLogger } from '@utils/general';
import { useDeviceConverter } from './device';

const log = getLogger('src:utils:client-upload:converters:source');

/**
 * A hook for converting source headers into data.
 * 
 * Returns a function for creating a source, retrieving a source, and a list of the
 * source headers.
 * 
 * @returns [function, function, string[]]
 */
export const useSourceConverter = () => {
  const [
    createDevice,
    _, // eslint-disable-line no-unused-vars
    deviceHeaderColumns,
  ] = useDeviceConverter();
  const savedSources = useRef(new Map());
  // a mapping of source header names to properties, as well as a function to process
  // the header value, if required.
  // the mapping is a plain object with the following properties:
  // - property (required): this is the GraphQL property name to use with the header name.
  // - processor (optional): a function to call for processing the header value.
  const headerMapping = useMemo(
    () => {
      return {
        'Stream Profile Name': {
          property: 'name',
        },
        'Stream Profile Description': {
          property: 'description',
        },
        'Protocol': {
          property: 'scheme',
        },
        'Path': {
          property: 'path',
          processor(row, input, value) {
            if (is.empty(value)) {
              return;
            }
            return value;
          },
        },
        'Port': {
          property: 'port',
          processor(row, input, value) {
            if (is.empty(value)) {
              return;
            }
            if (/^\d+$/.test(value)) {
              return parseInt(value, 10);
            }
            log.error('A non-integer was provided as a port in the row');
          },
        },
        'SDP Input': {
          property: 'sdpInput',
          processor(row, input, value) {
            if (is.empty(value)) {
              return;
            }
            return value;
          },
        },
        'Stream Profile Appliance ID': {
          property: 'applianceId',
          processor(row, input, value) {
            if (is.empty(value)) {
              return;
            }
            return value;
          },
        },
        'Stream Profile Camera ID': {
          property: 'deviceId',
          processor(row, input, value) {
            if (is.empty(value)) {
              return;
            }
            return value;
          },
        },
        'Camera Name': {
          property: 'deviceId',
          async processor(row, input, value) {
            if (!is.undef(input.deviceId)) {
              return;
            }
            if (is.empty(value)) {
              return;
            }
            const data = await createDevice(row);
            return data.id;
          },
        },

      };
    },
    [createDevice]
  );
  // a concatenated list of the source header columns and device header columns.
  const headerColumns = useMemo(
    () => Array.from(new Set(Object.keys(headerMapping).concat(deviceHeaderColumns))),
    [headerMapping, deviceHeaderColumns]
  );
  // a function for retrieving the mapping information for a column.
  const getMapping = useCallback(
    /**
     * A function for retrieving the mapping information for a column.
     * 
     * If the column name has an associated mapping (i.e.: plain object), it will be returned;
     * otherwise, undefined will be returned
     * @param {string} columnName The name of the column to use.
     * @returns {({property, processor?} | undefined)}
     */
    (columnName) => {
      const response = headerMapping[columnName];
      if (is.object(response)) {
        return response;
      }
    },
    [headerMapping]
  );
  const getReverseMapping = useCallback(
    /**
     * Given a GraphQL property name, this function will return the header names which map to it.
     * 
     * @param {string} property The name of the property to search for.
     * @returns {string[]}
     */
    (property) => {
      const columnNames = [];
      for (const [columnName, properties] of Object.entries(headerMapping)) {
        if (properties.property === property) {
          columnNames.push(columnName);
        }
      }
      return columnNames;
    },
    [headerMapping]
  );
  const storeSource = useCallback(
    /**
     * Stores a saved/cached copy of the source by source name.
     * 
     * This is to prevent many calls to the SSoT during bulk import.
     * 
     * @param {object} source The source object retrieved from the SSoT.
     * @returns {void}
     */
    (source) => {
      if (savedSources.current.has(source.name)) {
        return;
      }
      savedSources.current.set(source.name, source);
    },
    [savedSources]
  );
  const retrieveSource = useCallback(
    /**
     * Retrieves a source by name.
     * 
     * The function will first check the internal cache for the requested source. If it is found the that source
     * will be returned. Otherwise, it will check the SSoT for the corresponding source. If matches are found,
     * the first will be returned.
     * 
     * If not matches are found, then undefined is returned.
     * 
     * @param {string} name The name of the source to retrieve.
     * @returns {(object | undefined)}
     */
    async (name) => {
      if (is.undef(name)) {
        return;
      }
      if (savedSources.current.has(name)) {
        return savedSources.current.get(name);
      }
      const { query, execute } = getSources();
      query.addReturnProperties('id', 'name');
      query.addCriteria('name', name);
      const response = await execute();
      if (response.error) {
        return;
      }
      if (response.data.length > 0) {
        const device = response.data[0];
        savedSources.current.set(device.name, device);
        return device;
      }
    },
    [savedSources]
  );
  const convert = useCallback(
    /**
     * Processes a representation of the current row being evaluated.
     * 
     * @param {object} row An object containing the header names and the associated values.
     * @returns {Promise.<object>}
     */
    async (row) => {
      const input = {};
      for (const [header, value] of Object.entries(headerMapping)) {
        const { property, processor } = value;
        const rowValue = row[header];
        const _value = is.function(processor)
          ? await processor(row, input, rowValue)
          : rowValue;

        if (_value === undefined) {
          continue;
        }
        input[property] = _value;
      }
      return input;
    },
    [headerMapping]
  );
  const createSource = useCallback(
    /**
     * Creates or retrieves a source using the row data.
     * 
     * The function first checks if the source is in the cache or the SSoT. If not, it constructs
     * an input object to create the source.
     * 
     * @param {object} row An object containing the header names and the associated values.
     * @returns {Promise.<object>}
     */
    async (row) => {
      const names = getReverseMapping('name');
      for (const headerName of names) {
        const source = await retrieveSource(row[headerName]);
        if (is.defined(source)) {
          return source;
        }
      }
      const input = await convert(row, getMapping);
      input['category'] = 'Standard';
      const { mutation, execute } = saveSource(undefined, input);
      mutation.addReturnProperties('id', 'name');
      const response = await execute();
      if (response.error) {
        throw new Error(response.error);
      }
      storeSource(response.data);
      return response.data;
    },
    [
      storeSource,
      retrieveSource,
      getReverseMapping,
      getMapping,
      convert,
    ]
  );
  return [
    createSource,
    retrieveSource,
    headerColumns,
  ];
};